import {
  prepareEvent,
  prepareContractCall,
  readContract,
  type BaseTransactionOptions,
  type AbiParameterToPrimitiveType,
} from "thirdweb";

/**
* Contract events
*/

/**
 * Represents the filters for the "Approval" event.
 */
export type ApprovalEventFilters = Partial<{
  owner: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"owner","type":"address"}>
spender: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"spender","type":"address"}>
}>;

/**
 * Creates an event object for the Approval event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { approvalEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  approvalEvent({
 *  owner: ...,
 *  spender: ...,
 * })
 * ],
 * });
 * ```
 */
export function approvalEvent(filters: ApprovalEventFilters = {}) {
  return prepareEvent({
    signature: "event Approval(address indexed owner, address indexed spender, uint256 amount)",
    filters,
  });
};
  

/**
 * Represents the filters for the "AssetMoved" event.
 */
export type AssetMovedEventFilters = Partial<{
  from: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"from","type":"address"}>
to: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"to","type":"address"}>
assetAddress: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"assetAddress","type":"address"}>
}>;

/**
 * Creates an event object for the AssetMoved event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { assetMovedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  assetMovedEvent({
 *  from: ...,
 *  to: ...,
 *  assetAddress: ...,
 * })
 * ],
 * });
 * ```
 */
export function assetMovedEvent(filters: AssetMovedEventFilters = {}) {
  return prepareEvent({
    signature: "event AssetMoved(address indexed from, address indexed to, address indexed assetAddress, uint256 tokenId, uint256 amount, uint8 assetType)",
    filters,
  });
};
  



/**
 * Creates an event object for the AssetRemovedFromWhitelist event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { assetRemovedFromWhitelistEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  assetRemovedFromWhitelistEvent()
 * ],
 * });
 * ```
 */
export function assetRemovedFromWhitelistEvent() {
  return prepareEvent({
    signature: "event AssetRemovedFromWhitelist(address asset)",
  });
};
  



/**
 * Creates an event object for the AssetWhitelisted event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { assetWhitelistedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  assetWhitelistedEvent()
 * ],
 * });
 * ```
 */
export function assetWhitelistedEvent() {
  return prepareEvent({
    signature: "event AssetWhitelisted(address asset)",
  });
};
  

/**
 * Represents the filters for the "BundleConfiguredAndFinalized" event.
 */
export type BundleConfiguredAndFinalizedEventFilters = Partial<{
  owner: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"owner","type":"address"}>
at: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"uint256","name":"at","type":"uint256"}>
}>;

/**
 * Creates an event object for the BundleConfiguredAndFinalized event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { bundleConfiguredAndFinalizedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  bundleConfiguredAndFinalizedEvent({
 *  owner: ...,
 *  at: ...,
 * })
 * ],
 * });
 * ```
 */
export function bundleConfiguredAndFinalizedEvent(filters: BundleConfiguredAndFinalizedEventFilters = {}) {
  return prepareEvent({
    signature: "event BundleConfiguredAndFinalized(address indexed owner, uint256 indexed at, string _tokenKya, uint256 _initialMintAmount)",
    filters,
  });
};
  

/**
 * Represents the filters for the "BundleDeactivated" event.
 */
export type BundleDeactivatedEventFilters = Partial<{
  caller: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"caller","type":"address"}>
at: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"uint256","name":"at","type":"uint256"}>
}>;

/**
 * Creates an event object for the BundleDeactivated event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { bundleDeactivatedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  bundleDeactivatedEvent({
 *  caller: ...,
 *  at: ...,
 * })
 * ],
 * });
 * ```
 */
export function bundleDeactivatedEvent(filters: BundleDeactivatedEventFilters = {}) {
  return prepareEvent({
    signature: "event BundleDeactivated(address indexed caller, uint256 indexed at)",
    filters,
  });
};
  

/**
 * Represents the filters for the "BundleKyaChanged" event.
 */
export type BundleKyaChangedEventFilters = Partial<{
  by: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"by","type":"address"}>
}>;

/**
 * Creates an event object for the BundleKyaChanged event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { bundleKyaChangedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  bundleKyaChangedEvent({
 *  by: ...,
 * })
 * ],
 * });
 * ```
 */
export function bundleKyaChangedEvent(filters: BundleKyaChangedEventFilters = {}) {
  return prepareEvent({
    signature: "event BundleKyaChanged(address indexed by, string kya)",
    filters,
  });
};
  

/**
 * Represents the filters for the "BundleStorageChanged" event.
 */
export type BundleStorageChangedEventFilters = Partial<{
  by: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"by","type":"address"}>
}>;

/**
 * Creates an event object for the BundleStorageChanged event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { bundleStorageChangedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  bundleStorageChangedEvent({
 *  by: ...,
 * })
 * ],
 * });
 * ```
 */
export function bundleStorageChangedEvent(filters: BundleStorageChangedEventFilters = {}) {
  return prepareEvent({
    signature: "event BundleStorageChanged(address indexed by, address newBundleStorage)",
    filters,
  });
};
  

/**
 * Represents the filters for the "InitialAssetsAdded" event.
 */
export type InitialAssetsAddedEventFilters = Partial<{
  from: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"from","type":"address"}>
at: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"uint256","name":"at","type":"uint256"}>
}>;

/**
 * Creates an event object for the InitialAssetsAdded event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { initialAssetsAddedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  initialAssetsAddedEvent({
 *  from: ...,
 *  at: ...,
 * })
 * ],
 * });
 * ```
 */
export function initialAssetsAddedEvent(filters: InitialAssetsAddedEventFilters = {}) {
  return prepareEvent({
    signature: "event InitialAssetsAdded(address indexed from, uint256 indexed at)",
    filters,
  });
};
  

/**
 * Represents the filters for the "OwnershipHandoverCanceled" event.
 */
export type OwnershipHandoverCanceledEventFilters = Partial<{
  pendingOwner: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"pendingOwner","type":"address"}>
}>;

/**
 * Creates an event object for the OwnershipHandoverCanceled event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { ownershipHandoverCanceledEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  ownershipHandoverCanceledEvent({
 *  pendingOwner: ...,
 * })
 * ],
 * });
 * ```
 */
export function ownershipHandoverCanceledEvent(filters: OwnershipHandoverCanceledEventFilters = {}) {
  return prepareEvent({
    signature: "event OwnershipHandoverCanceled(address indexed pendingOwner)",
    filters,
  });
};
  

/**
 * Represents the filters for the "OwnershipHandoverRequested" event.
 */
export type OwnershipHandoverRequestedEventFilters = Partial<{
  pendingOwner: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"pendingOwner","type":"address"}>
}>;

/**
 * Creates an event object for the OwnershipHandoverRequested event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { ownershipHandoverRequestedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  ownershipHandoverRequestedEvent({
 *  pendingOwner: ...,
 * })
 * ],
 * });
 * ```
 */
export function ownershipHandoverRequestedEvent(filters: OwnershipHandoverRequestedEventFilters = {}) {
  return prepareEvent({
    signature: "event OwnershipHandoverRequested(address indexed pendingOwner)",
    filters,
  });
};
  

/**
 * Represents the filters for the "OwnershipTransferred" event.
 */
export type OwnershipTransferredEventFilters = Partial<{
  oldOwner: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"oldOwner","type":"address"}>
newOwner: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"newOwner","type":"address"}>
}>;

/**
 * Creates an event object for the OwnershipTransferred event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { ownershipTransferredEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  ownershipTransferredEvent({
 *  oldOwner: ...,
 *  newOwner: ...,
 * })
 * ],
 * });
 * ```
 */
export function ownershipTransferredEvent(filters: OwnershipTransferredEventFilters = {}) {
  return prepareEvent({
    signature: "event OwnershipTransferred(address indexed oldOwner, address indexed newOwner)",
    filters,
  });
};
  

/**
 * Represents the filters for the "RoleSet" event.
 */
export type RoleSetEventFilters = Partial<{
  holder: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"holder","type":"address"}>
role: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"uint256","name":"role","type":"uint256"}>
active: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"bool","name":"active","type":"bool"}>
}>;

/**
 * Creates an event object for the RoleSet event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { roleSetEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  roleSetEvent({
 *  holder: ...,
 *  role: ...,
 *  active: ...,
 * })
 * ],
 * });
 * ```
 */
export function roleSetEvent(filters: RoleSetEventFilters = {}) {
  return prepareEvent({
    signature: "event RoleSet(address indexed holder, uint256 indexed role, bool indexed active)",
    filters,
  });
};
  

/**
 * Represents the filters for the "Transfer" event.
 */
export type TransferEventFilters = Partial<{
  from: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"from","type":"address"}>
to: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"to","type":"address"}>
}>;

/**
 * Creates an event object for the Transfer event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { transferEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  transferEvent({
 *  from: ...,
 *  to: ...,
 * })
 * ],
 * });
 * ```
 */
export function transferEvent(filters: TransferEventFilters = {}) {
  return prepareEvent({
    signature: "event Transfer(address indexed from, address indexed to, uint256 amount)",
    filters,
  });
};
  

/**
* Contract read functions
*/



/**
 * Calls the "BURNER_ROLE" function on the contract.
 * @param options - The options for the BURNER_ROLE function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { BURNER_ROLE } from "TODO";
 *
 * const result = await BURNER_ROLE();
 *
 * ```
 */
export async function BURNER_ROLE(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x282c51f3",
  [],
  [
    {
      "internalType": "uint256",
      "name": "",
      "type": "uint256"
    }
  ]
],
    params: []
  });
};




/**
 * Calls the "DOMAIN_SEPARATOR" function on the contract.
 * @param options - The options for the DOMAIN_SEPARATOR function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { DOMAIN_SEPARATOR } from "TODO";
 *
 * const result = await DOMAIN_SEPARATOR();
 *
 * ```
 */
export async function DOMAIN_SEPARATOR(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x3644e515",
  [],
  [
    {
      "internalType": "bytes32",
      "name": "result",
      "type": "bytes32"
    }
  ]
],
    params: []
  });
};




/**
 * Calls the "MINTER_ROLE" function on the contract.
 * @param options - The options for the MINTER_ROLE function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { MINTER_ROLE } from "TODO";
 *
 * const result = await MINTER_ROLE();
 *
 * ```
 */
export async function MINTER_ROLE(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0xd5391393",
  [],
  [
    {
      "internalType": "uint256",
      "name": "",
      "type": "uint256"
    }
  ]
],
    params: []
  });
};




/**
 * Calls the "ONE_CARBON_WEIGHT" function on the contract.
 * @param options - The options for the ONE_CARBON_WEIGHT function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { ONE_CARBON_WEIGHT } from "TODO";
 *
 * const result = await ONE_CARBON_WEIGHT();
 *
 * ```
 */
export async function ONE_CARBON_WEIGHT(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0xc071bc87",
  [],
  [
    {
      "internalType": "uint256",
      "name": "",
      "type": "uint256"
    }
  ]
],
    params: []
  });
};


/**
 * Represents the parameters for the "allowance" function.
 */
export type AllowanceParams = {
  owner: AbiParameterToPrimitiveType<{"internalType":"address","name":"owner","type":"address"}>
spender: AbiParameterToPrimitiveType<{"internalType":"address","name":"spender","type":"address"}>
};

/**
 * Calls the "allowance" function on the contract.
 * @param options - The options for the allowance function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { allowance } from "TODO";
 *
 * const result = await allowance({
 *  owner: ...,
 *  spender: ...,
 * });
 *
 * ```
 */
export async function allowance(
  options: BaseTransactionOptions<AllowanceParams>
) {
  return readContract({
    contract: options.contract,
    method: [
  "0xdd62ed3e",
  [
    {
      "internalType": "address",
      "name": "owner",
      "type": "address"
    },
    {
      "internalType": "address",
      "name": "spender",
      "type": "address"
    }
  ],
  [
    {
      "internalType": "uint256",
      "name": "result",
      "type": "uint256"
    }
  ]
],
    params: [options.owner, options.spender]
  });
};


/**
 * Represents the parameters for the "assetsInBundle" function.
 */
export type AssetsInBundleParams = {
  arg_0: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"","type":"uint256"}>
};

/**
 * Calls the "assetsInBundle" function on the contract.
 * @param options - The options for the assetsInBundle function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { assetsInBundle } from "TODO";
 *
 * const result = await assetsInBundle({
 *  arg_0: ...,
 * });
 *
 * ```
 */
export async function assetsInBundle(
  options: BaseTransactionOptions<AssetsInBundleParams>
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x9c1ccf9a",
  [
    {
      "internalType": "uint256",
      "name": "",
      "type": "uint256"
    }
  ],
  [
    {
      "internalType": "address",
      "name": "assetAddress",
      "type": "address"
    },
    {
      "internalType": "uint256",
      "name": "tokenId",
      "type": "uint256"
    },
    {
      "internalType": "uint256",
      "name": "amount",
      "type": "uint256"
    },
    {
      "internalType": "enum AssetType",
      "name": "assetType",
      "type": "uint8"
    }
  ]
],
    params: [options.arg_0]
  });
};


/**
 * Represents the parameters for the "balanceOf" function.
 */
export type BalanceOfParams = {
  owner: AbiParameterToPrimitiveType<{"internalType":"address","name":"owner","type":"address"}>
};

/**
 * Calls the "balanceOf" function on the contract.
 * @param options - The options for the balanceOf function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { balanceOf } from "TODO";
 *
 * const result = await balanceOf({
 *  owner: ...,
 * });
 *
 * ```
 */
export async function balanceOf(
  options: BaseTransactionOptions<BalanceOfParams>
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x70a08231",
  [
    {
      "internalType": "address",
      "name": "owner",
      "type": "address"
    }
  ],
  [
    {
      "internalType": "uint256",
      "name": "result",
      "type": "uint256"
    }
  ]
],
    params: [options.owner]
  });
};




/**
 * Calls the "bundleStorage" function on the contract.
 * @param options - The options for the bundleStorage function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { bundleStorage } from "TODO";
 *
 * const result = await bundleStorage();
 *
 * ```
 */
export async function bundleStorage(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x5f63303d",
  [],
  [
    {
      "internalType": "address",
      "name": "",
      "type": "address"
    }
  ]
],
    params: []
  });
};




/**
 * Calls the "decimals" function on the contract.
 * @param options - The options for the decimals function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { decimals } from "TODO";
 *
 * const result = await decimals();
 *
 * ```
 */
export async function decimals(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x313ce567",
  [],
  [
    {
      "internalType": "uint8",
      "name": "",
      "type": "uint8"
    }
  ]
],
    params: []
  });
};


/**
 * Represents the parameters for the "hasRole" function.
 */
export type HasRoleParams = {
  holder: AbiParameterToPrimitiveType<{"internalType":"address","name":"holder","type":"address"}>
role: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"role","type":"uint256"}>
};

/**
 * Calls the "hasRole" function on the contract.
 * @param options - The options for the hasRole function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { hasRole } from "TODO";
 *
 * const result = await hasRole({
 *  holder: ...,
 *  role: ...,
 * });
 *
 * ```
 */
export async function hasRole(
  options: BaseTransactionOptions<HasRoleParams>
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x5c97f4a2",
  [
    {
      "internalType": "address",
      "name": "holder",
      "type": "address"
    },
    {
      "internalType": "uint256",
      "name": "role",
      "type": "uint256"
    }
  ],
  [
    {
      "internalType": "bool",
      "name": "result",
      "type": "bool"
    }
  ]
],
    params: [options.holder, options.role]
  });
};




/**
 * Calls the "lastOperationTimestamp" function on the contract.
 * @param options - The options for the lastOperationTimestamp function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { lastOperationTimestamp } from "TODO";
 *
 * const result = await lastOperationTimestamp();
 *
 * ```
 */
export async function lastOperationTimestamp(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x6d1a8664",
  [],
  [
    {
      "internalType": "uint256",
      "name": "",
      "type": "uint256"
    }
  ]
],
    params: []
  });
};




/**
 * Calls the "name" function on the contract.
 * @param options - The options for the name function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { name } from "TODO";
 *
 * const result = await name();
 *
 * ```
 */
export async function name(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x06fdde03",
  [],
  [
    {
      "internalType": "string",
      "name": "",
      "type": "string"
    }
  ]
],
    params: []
  });
};


/**
 * Represents the parameters for the "nonces" function.
 */
export type NoncesParams = {
  owner: AbiParameterToPrimitiveType<{"internalType":"address","name":"owner","type":"address"}>
};

/**
 * Calls the "nonces" function on the contract.
 * @param options - The options for the nonces function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { nonces } from "TODO";
 *
 * const result = await nonces({
 *  owner: ...,
 * });
 *
 * ```
 */
export async function nonces(
  options: BaseTransactionOptions<NoncesParams>
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x7ecebe00",
  [
    {
      "internalType": "address",
      "name": "owner",
      "type": "address"
    }
  ],
  [
    {
      "internalType": "uint256",
      "name": "result",
      "type": "uint256"
    }
  ]
],
    params: [options.owner]
  });
};




/**
 * Calls the "owner" function on the contract.
 * @param options - The options for the owner function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { owner } from "TODO";
 *
 * const result = await owner();
 *
 * ```
 */
export async function owner(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x8da5cb5b",
  [],
  [
    {
      "internalType": "address",
      "name": "result",
      "type": "address"
    }
  ]
],
    params: []
  });
};


/**
 * Represents the parameters for the "ownershipHandoverExpiresAt" function.
 */
export type OwnershipHandoverExpiresAtParams = {
  pendingOwner: AbiParameterToPrimitiveType<{"internalType":"address","name":"pendingOwner","type":"address"}>
};

/**
 * Calls the "ownershipHandoverExpiresAt" function on the contract.
 * @param options - The options for the ownershipHandoverExpiresAt function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { ownershipHandoverExpiresAt } from "TODO";
 *
 * const result = await ownershipHandoverExpiresAt({
 *  pendingOwner: ...,
 * });
 *
 * ```
 */
export async function ownershipHandoverExpiresAt(
  options: BaseTransactionOptions<OwnershipHandoverExpiresAtParams>
) {
  return readContract({
    contract: options.contract,
    method: [
  "0xfee81cf4",
  [
    {
      "internalType": "address",
      "name": "pendingOwner",
      "type": "address"
    }
  ],
  [
    {
      "internalType": "uint256",
      "name": "result",
      "type": "uint256"
    }
  ]
],
    params: [options.pendingOwner]
  });
};


/**
 * Represents the parameters for the "roleHolderAt" function.
 */
export type RoleHolderAtParams = {
  role: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"role","type":"uint256"}>
i: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"i","type":"uint256"}>
};

/**
 * Calls the "roleHolderAt" function on the contract.
 * @param options - The options for the roleHolderAt function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { roleHolderAt } from "TODO";
 *
 * const result = await roleHolderAt({
 *  role: ...,
 *  i: ...,
 * });
 *
 * ```
 */
export async function roleHolderAt(
  options: BaseTransactionOptions<RoleHolderAtParams>
) {
  return readContract({
    contract: options.contract,
    method: [
  "0xe3b3ac43",
  [
    {
      "internalType": "uint256",
      "name": "role",
      "type": "uint256"
    },
    {
      "internalType": "uint256",
      "name": "i",
      "type": "uint256"
    }
  ],
  [
    {
      "internalType": "address",
      "name": "result",
      "type": "address"
    }
  ]
],
    params: [options.role, options.i]
  });
};


/**
 * Represents the parameters for the "roleHolderCount" function.
 */
export type RoleHolderCountParams = {
  role: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"role","type":"uint256"}>
};

/**
 * Calls the "roleHolderCount" function on the contract.
 * @param options - The options for the roleHolderCount function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { roleHolderCount } from "TODO";
 *
 * const result = await roleHolderCount({
 *  role: ...,
 * });
 *
 * ```
 */
export async function roleHolderCount(
  options: BaseTransactionOptions<RoleHolderCountParams>
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x492ba875",
  [
    {
      "internalType": "uint256",
      "name": "role",
      "type": "uint256"
    }
  ],
  [
    {
      "internalType": "uint256",
      "name": "result",
      "type": "uint256"
    }
  ]
],
    params: [options.role]
  });
};


/**
 * Represents the parameters for the "roleHolders" function.
 */
export type RoleHoldersParams = {
  role: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"role","type":"uint256"}>
};

/**
 * Calls the "roleHolders" function on the contract.
 * @param options - The options for the roleHolders function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { roleHolders } from "TODO";
 *
 * const result = await roleHolders({
 *  role: ...,
 * });
 *
 * ```
 */
export async function roleHolders(
  options: BaseTransactionOptions<RoleHoldersParams>
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x84cc10c5",
  [
    {
      "internalType": "uint256",
      "name": "role",
      "type": "uint256"
    }
  ],
  [
    {
      "internalType": "address[]",
      "name": "result",
      "type": "address[]"
    }
  ]
],
    params: [options.role]
  });
};




/**
 * Calls the "symbol" function on the contract.
 * @param options - The options for the symbol function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { symbol } from "TODO";
 *
 * const result = await symbol();
 *
 * ```
 */
export async function symbol(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x95d89b41",
  [],
  [
    {
      "internalType": "string",
      "name": "",
      "type": "string"
    }
  ]
],
    params: []
  });
};




/**
 * Calls the "tokenKya" function on the contract.
 * @param options - The options for the tokenKya function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { tokenKya } from "TODO";
 *
 * const result = await tokenKya();
 *
 * ```
 */
export async function tokenKya(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x6a0c7c3f",
  [],
  [
    {
      "internalType": "string",
      "name": "",
      "type": "string"
    }
  ]
],
    params: []
  });
};




/**
 * Calls the "totalSupply" function on the contract.
 * @param options - The options for the totalSupply function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { totalSupply } from "TODO";
 *
 * const result = await totalSupply();
 *
 * ```
 */
export async function totalSupply(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x18160ddd",
  [],
  [
    {
      "internalType": "uint256",
      "name": "result",
      "type": "uint256"
    }
  ]
],
    params: []
  });
};


/**
 * Represents the parameters for the "whitelistedAssets" function.
 */
export type WhitelistedAssetsParams = {
  arg_0: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"","type":"uint256"}>
};

/**
 * Calls the "whitelistedAssets" function on the contract.
 * @param options - The options for the whitelistedAssets function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { whitelistedAssets } from "TODO";
 *
 * const result = await whitelistedAssets({
 *  arg_0: ...,
 * });
 *
 * ```
 */
export async function whitelistedAssets(
  options: BaseTransactionOptions<WhitelistedAssetsParams>
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x1fde3c75",
  [
    {
      "internalType": "uint256",
      "name": "",
      "type": "uint256"
    }
  ],
  [
    {
      "internalType": "address",
      "name": "",
      "type": "address"
    }
  ]
],
    params: [options.arg_0]
  });
};


/**
* Contract write functions
*/

/**
 * Represents the parameters for the "addNewAssets" function.
 */
export type AddNewAssetsParams = {
  assets: AbiParameterToPrimitiveType<{"components":[{"internalType":"address","name":"assetAddress","type":"address"},{"internalType":"uint256","name":"tokenId","type":"uint256"},{"internalType":"uint256","name":"amount","type":"uint256"},{"internalType":"enum AssetType","name":"assetType","type":"uint8"}],"internalType":"struct Asset[]","name":"assets","type":"tuple[]"}>
};

/**
 * Calls the "addNewAssets" function on the contract.
 * @param options - The options for the "addNewAssets" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { addNewAssets } from "TODO";
 *
 * const transaction = addNewAssets({
 *  assets: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function addNewAssets(
  options: BaseTransactionOptions<AddNewAssetsParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0xa7b0e1f9",
  [
    {
      "components": [
        {
          "internalType": "address",
          "name": "assetAddress",
          "type": "address"
        },
        {
          "internalType": "uint256",
          "name": "tokenId",
          "type": "uint256"
        },
        {
          "internalType": "uint256",
          "name": "amount",
          "type": "uint256"
        },
        {
          "internalType": "enum AssetType",
          "name": "assetType",
          "type": "uint8"
        }
      ],
      "internalType": "struct Asset[]",
      "name": "assets",
      "type": "tuple[]"
    }
  ],
  []
],
    params: [options.assets]
  });
};


/**
 * Represents the parameters for the "approve" function.
 */
export type ApproveParams = {
  spender: AbiParameterToPrimitiveType<{"internalType":"address","name":"spender","type":"address"}>
amount: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"amount","type":"uint256"}>
};

/**
 * Calls the "approve" function on the contract.
 * @param options - The options for the "approve" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { approve } from "TODO";
 *
 * const transaction = approve({
 *  spender: ...,
 *  amount: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function approve(
  options: BaseTransactionOptions<ApproveParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x095ea7b3",
  [
    {
      "internalType": "address",
      "name": "spender",
      "type": "address"
    },
    {
      "internalType": "uint256",
      "name": "amount",
      "type": "uint256"
    }
  ],
  [
    {
      "internalType": "bool",
      "name": "",
      "type": "bool"
    }
  ]
],
    params: [options.spender, options.amount]
  });
};


/**
 * Represents the parameters for the "burn" function.
 */
export type BurnParams = {
  amount: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"amount","type":"uint256"}>
};

/**
 * Calls the "burn" function on the contract.
 * @param options - The options for the "burn" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { burn } from "TODO";
 *
 * const transaction = burn({
 *  amount: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function burn(
  options: BaseTransactionOptions<BurnParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x42966c68",
  [
    {
      "internalType": "uint256",
      "name": "amount",
      "type": "uint256"
    }
  ],
  []
],
    params: [options.amount]
  });
};




/**
 * Calls the "cancelOwnershipHandover" function on the contract.
 * @param options - The options for the "cancelOwnershipHandover" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { cancelOwnershipHandover } from "TODO";
 *
 * const transaction = cancelOwnershipHandover();
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function cancelOwnershipHandover(
  options: BaseTransactionOptions
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x54d1f13d",
  [],
  []
],
    params: []
  });
};


/**
 * Represents the parameters for the "changeBundleStorage" function.
 */
export type ChangeBundleStorageParams = {
  newBundleStorage: AbiParameterToPrimitiveType<{"internalType":"address","name":"newBundleStorage","type":"address"}>
};

/**
 * Calls the "changeBundleStorage" function on the contract.
 * @param options - The options for the "changeBundleStorage" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { changeBundleStorage } from "TODO";
 *
 * const transaction = changeBundleStorage({
 *  newBundleStorage: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function changeBundleStorage(
  options: BaseTransactionOptions<ChangeBundleStorageParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x3465013d",
  [
    {
      "internalType": "address",
      "name": "newBundleStorage",
      "type": "address"
    }
  ],
  []
],
    params: [options.newBundleStorage]
  });
};


/**
 * Represents the parameters for the "completeOwnershipHandover" function.
 */
export type CompleteOwnershipHandoverParams = {
  pendingOwner: AbiParameterToPrimitiveType<{"internalType":"address","name":"pendingOwner","type":"address"}>
};

/**
 * Calls the "completeOwnershipHandover" function on the contract.
 * @param options - The options for the "completeOwnershipHandover" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { completeOwnershipHandover } from "TODO";
 *
 * const transaction = completeOwnershipHandover({
 *  pendingOwner: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function completeOwnershipHandover(
  options: BaseTransactionOptions<CompleteOwnershipHandoverParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0xf04e283e",
  [
    {
      "internalType": "address",
      "name": "pendingOwner",
      "type": "address"
    }
  ],
  []
],
    params: [options.pendingOwner]
  });
};




/**
 * Calls the "dissolve" function on the contract.
 * @param options - The options for the "dissolve" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { dissolve } from "TODO";
 *
 * const transaction = dissolve();
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function dissolve(
  options: BaseTransactionOptions
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x3bef8a3a",
  [],
  []
],
    params: []
  });
};


/**
 * Represents the parameters for the "mint" function.
 */
export type MintParams = {
  account: AbiParameterToPrimitiveType<{"internalType":"address","name":"account","type":"address"}>
amount: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"amount","type":"uint256"}>
};

/**
 * Calls the "mint" function on the contract.
 * @param options - The options for the "mint" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { mint } from "TODO";
 *
 * const transaction = mint({
 *  account: ...,
 *  amount: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function mint(
  options: BaseTransactionOptions<MintParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x40c10f19",
  [
    {
      "internalType": "address",
      "name": "account",
      "type": "address"
    },
    {
      "internalType": "uint256",
      "name": "amount",
      "type": "uint256"
    }
  ],
  []
],
    params: [options.account, options.amount]
  });
};


/**
 * Represents the parameters for the "permit" function.
 */
export type PermitParams = {
  owner: AbiParameterToPrimitiveType<{"internalType":"address","name":"owner","type":"address"}>
spender: AbiParameterToPrimitiveType<{"internalType":"address","name":"spender","type":"address"}>
value: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"value","type":"uint256"}>
deadline: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"deadline","type":"uint256"}>
v: AbiParameterToPrimitiveType<{"internalType":"uint8","name":"v","type":"uint8"}>
r: AbiParameterToPrimitiveType<{"internalType":"bytes32","name":"r","type":"bytes32"}>
s: AbiParameterToPrimitiveType<{"internalType":"bytes32","name":"s","type":"bytes32"}>
};

/**
 * Calls the "permit" function on the contract.
 * @param options - The options for the "permit" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { permit } from "TODO";
 *
 * const transaction = permit({
 *  owner: ...,
 *  spender: ...,
 *  value: ...,
 *  deadline: ...,
 *  v: ...,
 *  r: ...,
 *  s: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function permit(
  options: BaseTransactionOptions<PermitParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0xd505accf",
  [
    {
      "internalType": "address",
      "name": "owner",
      "type": "address"
    },
    {
      "internalType": "address",
      "name": "spender",
      "type": "address"
    },
    {
      "internalType": "uint256",
      "name": "value",
      "type": "uint256"
    },
    {
      "internalType": "uint256",
      "name": "deadline",
      "type": "uint256"
    },
    {
      "internalType": "uint8",
      "name": "v",
      "type": "uint8"
    },
    {
      "internalType": "bytes32",
      "name": "r",
      "type": "bytes32"
    },
    {
      "internalType": "bytes32",
      "name": "s",
      "type": "bytes32"
    }
  ],
  []
],
    params: [options.owner, options.spender, options.value, options.deadline, options.v, options.r, options.s]
  });
};


/**
 * Represents the parameters for the "removeWhitelistedAsset" function.
 */
export type RemoveWhitelistedAssetParams = {
  asset: AbiParameterToPrimitiveType<{"internalType":"address","name":"asset","type":"address"}>
};

/**
 * Calls the "removeWhitelistedAsset" function on the contract.
 * @param options - The options for the "removeWhitelistedAsset" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { removeWhitelistedAsset } from "TODO";
 *
 * const transaction = removeWhitelistedAsset({
 *  asset: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function removeWhitelistedAsset(
  options: BaseTransactionOptions<RemoveWhitelistedAssetParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0xb6dfcd8b",
  [
    {
      "internalType": "address",
      "name": "asset",
      "type": "address"
    }
  ],
  []
],
    params: [options.asset]
  });
};




/**
 * Calls the "renounceOwnership" function on the contract.
 * @param options - The options for the "renounceOwnership" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { renounceOwnership } from "TODO";
 *
 * const transaction = renounceOwnership();
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function renounceOwnership(
  options: BaseTransactionOptions
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x715018a6",
  [],
  []
],
    params: []
  });
};




/**
 * Calls the "requestOwnershipHandover" function on the contract.
 * @param options - The options for the "requestOwnershipHandover" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { requestOwnershipHandover } from "TODO";
 *
 * const transaction = requestOwnershipHandover();
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function requestOwnershipHandover(
  options: BaseTransactionOptions
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x25692962",
  [],
  []
],
    params: []
  });
};


/**
 * Represents the parameters for the "setRole" function.
 */
export type SetRoleParams = {
  holder: AbiParameterToPrimitiveType<{"internalType":"address","name":"holder","type":"address"}>
role: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"role","type":"uint256"}>
active: AbiParameterToPrimitiveType<{"internalType":"bool","name":"active","type":"bool"}>
};

/**
 * Calls the "setRole" function on the contract.
 * @param options - The options for the "setRole" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { setRole } from "TODO";
 *
 * const transaction = setRole({
 *  holder: ...,
 *  role: ...,
 *  active: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function setRole(
  options: BaseTransactionOptions<SetRoleParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x5978cd29",
  [
    {
      "internalType": "address",
      "name": "holder",
      "type": "address"
    },
    {
      "internalType": "uint256",
      "name": "role",
      "type": "uint256"
    },
    {
      "internalType": "bool",
      "name": "active",
      "type": "bool"
    }
  ],
  []
],
    params: [options.holder, options.role, options.active]
  });
};


/**
 * Represents the parameters for the "transfer" function.
 */
export type TransferParams = {
  to: AbiParameterToPrimitiveType<{"internalType":"address","name":"to","type":"address"}>
amount: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"amount","type":"uint256"}>
};

/**
 * Calls the "transfer" function on the contract.
 * @param options - The options for the "transfer" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { transfer } from "TODO";
 *
 * const transaction = transfer({
 *  to: ...,
 *  amount: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function transfer(
  options: BaseTransactionOptions<TransferParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0xa9059cbb",
  [
    {
      "internalType": "address",
      "name": "to",
      "type": "address"
    },
    {
      "internalType": "uint256",
      "name": "amount",
      "type": "uint256"
    }
  ],
  [
    {
      "internalType": "bool",
      "name": "",
      "type": "bool"
    }
  ]
],
    params: [options.to, options.amount]
  });
};


/**
 * Represents the parameters for the "transferFrom" function.
 */
export type TransferFromParams = {
  from: AbiParameterToPrimitiveType<{"internalType":"address","name":"from","type":"address"}>
to: AbiParameterToPrimitiveType<{"internalType":"address","name":"to","type":"address"}>
amount: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"amount","type":"uint256"}>
};

/**
 * Calls the "transferFrom" function on the contract.
 * @param options - The options for the "transferFrom" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { transferFrom } from "TODO";
 *
 * const transaction = transferFrom({
 *  from: ...,
 *  to: ...,
 *  amount: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function transferFrom(
  options: BaseTransactionOptions<TransferFromParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x23b872dd",
  [
    {
      "internalType": "address",
      "name": "from",
      "type": "address"
    },
    {
      "internalType": "address",
      "name": "to",
      "type": "address"
    },
    {
      "internalType": "uint256",
      "name": "amount",
      "type": "uint256"
    }
  ],
  [
    {
      "internalType": "bool",
      "name": "",
      "type": "bool"
    }
  ]
],
    params: [options.from, options.to, options.amount]
  });
};


/**
 * Represents the parameters for the "transferOwnership" function.
 */
export type TransferOwnershipParams = {
  newOwner: AbiParameterToPrimitiveType<{"internalType":"address","name":"newOwner","type":"address"}>
};

/**
 * Calls the "transferOwnership" function on the contract.
 * @param options - The options for the "transferOwnership" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { transferOwnership } from "TODO";
 *
 * const transaction = transferOwnership({
 *  newOwner: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function transferOwnership(
  options: BaseTransactionOptions<TransferOwnershipParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0xf2fde38b",
  [
    {
      "internalType": "address",
      "name": "newOwner",
      "type": "address"
    }
  ],
  []
],
    params: [options.newOwner]
  });
};


/**
 * Represents the parameters for the "updateKYA" function.
 */
export type UpdateKYAParams = {
  kya: AbiParameterToPrimitiveType<{"internalType":"string","name":"kya","type":"string"}>
};

/**
 * Calls the "updateKYA" function on the contract.
 * @param options - The options for the "updateKYA" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { updateKYA } from "TODO";
 *
 * const transaction = updateKYA({
 *  kya: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function updateKYA(
  options: BaseTransactionOptions<UpdateKYAParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0xd6feb638",
  [
    {
      "internalType": "string",
      "name": "kya",
      "type": "string"
    }
  ],
  []
],
    params: [options.kya]
  });
};


/**
 * Represents the parameters for the "whitelistAssets" function.
 */
export type WhitelistAssetsParams = {
  assets: AbiParameterToPrimitiveType<{"internalType":"address[]","name":"assets","type":"address[]"}>
};

/**
 * Calls the "whitelistAssets" function on the contract.
 * @param options - The options for the "whitelistAssets" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { whitelistAssets } from "TODO";
 *
 * const transaction = whitelistAssets({
 *  assets: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function whitelistAssets(
  options: BaseTransactionOptions<WhitelistAssetsParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x358a8eb1",
  [
    {
      "internalType": "address[]",
      "name": "assets",
      "type": "address[]"
    }
  ],
  []
],
    params: [options.assets]
  });
};


/**
 * Represents the parameters for the "withdrawAssets" function.
 */
export type WithdrawAssetsParams = {
  assets: AbiParameterToPrimitiveType<{"components":[{"internalType":"address","name":"assetAddress","type":"address"},{"internalType":"uint256","name":"tokenId","type":"uint256"},{"internalType":"uint256","name":"amount","type":"uint256"},{"internalType":"enum AssetType","name":"assetType","type":"uint8"}],"internalType":"struct Asset[]","name":"assets","type":"tuple[]"}>
};

/**
 * Calls the "withdrawAssets" function on the contract.
 * @param options - The options for the "withdrawAssets" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { withdrawAssets } from "TODO";
 *
 * const transaction = withdrawAssets({
 *  assets: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function withdrawAssets(
  options: BaseTransactionOptions<WithdrawAssetsParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x8bdc5545",
  [
    {
      "components": [
        {
          "internalType": "address",
          "name": "assetAddress",
          "type": "address"
        },
        {
          "internalType": "uint256",
          "name": "tokenId",
          "type": "uint256"
        },
        {
          "internalType": "uint256",
          "name": "amount",
          "type": "uint256"
        },
        {
          "internalType": "enum AssetType",
          "name": "assetType",
          "type": "uint8"
        }
      ],
      "internalType": "struct Asset[]",
      "name": "assets",
      "type": "tuple[]"
    }
  ],
  []
],
    params: [options.assets]
  });
};


